import { CommonModule, CurrencyPipe, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnInit, Output, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { DialogConfirmNavigateAwayParkerComponent } from 'src/app/core/dialog/dialog-confirm-navigate-away-parker/dialog-confirm-navigate-away-parker.component';
import { AuthService } from 'src/app/core/services/auth-security/auth.service';
import { DataBroadcastService } from 'src/app/core/services/broadcast/data-broadcast.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { ClonerService } from 'src/app/core/services/helper/cloner.service';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import {
  getParkerMaxEndDate,
  getParkerMaxEndDateDisplay,
  isParkerEndDateBeyondMax,
} from 'src/app/shared/code/parker-date-rules';
import { ParkerStateService } from 'src/app/core/services/state/parker-state.service';
import { AceStates } from 'src/app/shared/data/states';
import { YearSelectorDirectiveStandalone } from 'src/app/shared/directives/set-date-current-year-standalone.directive';
import {
  IAdditionalEmails,
  IAdditionalVehicles,
  ICompaniesShortList,
  ILotRateList,
  ILotUsageMetrics,
  IParkerProfileDetail,
  IParkerProfileUpdateRequest,
  ISpaceEmailUsers,
  IStallTypes,
  IState,
  IParkerUpdateStatusResponse,
  IAmanoParkerDataForCardReassignmentResponse,
} from 'src/app/shared/interfaces';
import { environment } from 'src/environments/environment';
import { updateNowErrorMatcher } from 'src/app/core/code/helpers/update-now-error-matcher';
import { ProcessingBannerComponent } from 'src/app/shared/ui/processing-banner/processing-banner.component';
import { firstValueFrom } from 'rxjs';
import { DialogCardNumberReassignComponent } from 'src/app/core/dialog/dialog-card-number-reassign/dialog-card-number-reassign.component';
import { DialogAmanoOneReactivateComponent } from 'src/app/core/dialog/dialog-amano-one-reactivate/dialog-amano-one-reactivate.component';
import { ParcsDataService } from 'src/app/core/services/data/parcs-data.service';
import { IAmanoOneAccessHolderUpdateResponse } from 'src/app/shared/code/interfaces/parcs-interfaces';

@Component({
  selector: 'app-parkers-v2-edit',
  standalone: true,
  imports: [
    MatIconModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatDatepickerModule,
    MatSelectModule,
    CurrencyPipe,
    CommonModule,
    NgIf,
    YearSelectorDirectiveStandalone,
    ProcessingBannerComponent,
    MatTooltipModule,
  ],

  templateUrl: './parkers-v2-edit.component.html',
  styleUrl: './parkers-v2-edit.component.scss',
})
export class ParkersV2EditComponent implements OnInit {
  loading: boolean = true;
  parkerDetail: IParkerProfileDetail;
  originalParkerDetail: IParkerProfileDetail; // Tracks original input passed
  @Input() set parkerDetailInput(value: IParkerProfileDetail) {
    this.parkerDetail = this.clonerService.deepClone(value);
    this.originalParkerDetail = this.clonerService.deepClone(value);

    //this.loading = false;
  }
  @Output() editFinishedEvent = new EventEmitter<IParkerProfileDetail>();

  urlDomain: string = '';
  parkerId: string = '';
  //parker: IParkerProfileDetail = {} as IParkerProfileDetail;
  additionalVehicles: IAdditionalVehicles[] = [];
  additionalEmails: IAdditionalEmails[] = [];
  spaceEmails: ISpaceEmailUsers[] = [];
  gridFontSize: number = 12;
  currentlyActiveRow: number = -1;
  parkerEditForm: FormGroup;
  vehicleForm: FormGroup;
  emailForm: FormGroup;
  marketRateList: ILotRateList[] = [];
  stallDescriptions: IStallTypes[] = [];
  description: string = '';
  stallType: string = '';
  billingStates: IState[] = [];
  vehicleStates: IState[] = [];
  additionalVehicleStates: IState[] = [];
  rateDescriptionSummary: string = '';
  companyList: ICompaniesShortList[] = [];
  headerLine: string = '';
  addedParkerData: IParkerProfileDetail;
  patchedUpEndDate: string;
  vehicleSaveMode: boolean = false;
  additionalEmailSaveMode: boolean = false;
  spaceEmailValue: string = 'space@email.com';
  isFormDirty: boolean = false;
  disableSpaceTab: boolean;
  currentCompanyId: string = '';
  lotRateUsageMetrics: ILotUsageMetrics[] = [];
  lotNumber: string = '';
  vehicleLicenseInvalid: boolean = false;
  isProcessingSave: boolean = false;
  processingMessage: string = 'Processing - Stand by...';
  private keyCardValidationDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private isCardReassignmentPending: boolean = false;
  parkerReactivated: boolean = false;
  queueParkerReactivation: boolean = false;
  currentEndDate: Date;

  // Ceiling for the End Date, enforced by the datepicker and re-checked before submit.
  maxEndDate: Date = getParkerMaxEndDate();
  // Displayed with a 2-digit year on this screen only (e.g. 12/31/46) to save horizontal space;
  // the shared helper (4-digit year) is still used for the submit-time validation message.
  maxEndDateDisplay: string = getParkerMaxEndDateDisplay().replace(/(\d+)$/, (year) => year.slice(-2));
  queueAmanoOneReactivationMode: number = 0;
  amanoOneAccessHolder: IAmanoOneAccessHolderUpdateResponse;

  hasParc: boolean = false; // This parker needs to get PARC information during new Parker process
  parcsId: string = ''; // This is the PARC ID representing the PARCS used by this lot.

  // 20250821 :: Ken Moss - implements process so that error message are displayed immediately instead of on blur event
  errMatcher = new updateNowErrorMatcher();

  //Services
  _appGeneralState = inject(AppGeneralStateService);
  _dialogHelper = inject(MessageDialogService);
  _dialog = inject(MatDialog);
  _destroyRef = inject(DestroyRef);
  _parcsDataService = inject(ParcsDataService);

  constructor(
    private router: Router,
    private dataService: DataService,
    private parkerStateService: ParkerStateService,
    private dataBroadcastService: DataBroadcastService,
    private authService: AuthService,
    private clonerService: ClonerService,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
  ) {
    this.urlDomain = environment.UrlDomain;
    this._appGeneralState.canChangeLot = false;
    this.initParkerForm();
    this.hasParc = Object.keys(this._appGeneralState.currentLotParcsConfiguration).length !== 0;
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.GetLotRateUsageMetrics(this._appGeneralState.currentLotNumber);
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.keyCardValidationDebounceTimer !== null) {
      clearTimeout(this.keyCardValidationDebounceTimer);
    }

    this._appGeneralState.canChangeLot = true;
  }

  InitEditForm(): void {
    this.currentEndDate = this.originalParkerDetail.endDate;
    console.log('Current end date:', this.currentEndDate);
    this.initBillingStateList();
    this.initVehicleStateList();
    this.initAdditionalVehicleStateList();

    this.setHeaderLine();

    this.onInitDataFetch();
  }

  GetLotRateUsageMetrics(lotNumber: string) {
    this.dataService.GetLotUsageMetrics(lotNumber).subscribe(
      (res) => {
        this.lotRateUsageMetrics = res;
      },
      (err: any) => {},
      () => {
        this.InitEditForm();
      },
    );
  }

  onValidateVehicleLicense(): void {
    const make = this.parkerEditForm.get('vehicleMake')?.value ?? '';
    const model = this.parkerEditForm.get('vehicleModel')?.value ?? '';
    const year = this.parkerEditForm.get('vehicleYear')?.value ?? '';
    const state = this.parkerEditForm.get('vehicleState')?.value ?? '';
    const color = this.parkerEditForm.get('vehicleColor')?.value ?? '';
    const license = this.parkerEditForm.get('vehicleLicense')?.value ?? '';

    const isOtherLicenseDataPresent: boolean =
      make.trim().length > 0 ||
      model.trim().length > 0 ||
      year.toString().trim().length > 0 ||
      state.trim().length > 0 ||
      color.trim().length > 0;

    const vehicleLicenseDataMissing: boolean = license.trim().length === 0;

    if (vehicleLicenseDataMissing && isOtherLicenseDataPresent) {
      this.vehicleLicenseInvalid = true;
      this.parkerEditForm.controls['vehicleLicense'].setErrors({ vehicleLicense: true });
    } else {
      this.vehicleLicenseInvalid = false;
      this.parkerEditForm.controls['vehicleLicense'].setErrors(null);
    }
  }

  isValidParkerId(parkerId: string): boolean {
    let isValid: boolean = true;
    const targetParker = this.parkerStateService.filteredParkers.find((parker) => parker.parkerId == parkerId);

    if (!targetParker) return false;

    isValid = targetParker.lastName == this.originalParkerDetail.lastName ? true : false;

    if (isValid) isValid = targetParker.firstName == this.originalParkerDetail.firstName ? true : false;

    if (isValid) isValid = targetParker.companyId == this.originalParkerDetail.companyId ? true : false;

    if (isValid) isValid = targetParker.startDate == this.originalParkerDetail.startDate ? true : false;

    return isValid;
  }

  onInitSetStartupParkerFormData(): void {
    if (this.parkerDetail.companyId === '') this.getMarketRateListByLotNumber(this.parkerDetail.lotNumber);
    else {
      this.getCompanyAndMarketRateListByLotNumber(this.parkerDetail.lotNumber, this.parkerDetail.companyId);
      this.parkerEditForm.patchValue({
        companyId: this.parkerDetail.companyId,
      });
    }

    this.checkIfFormIsDirty();
  }

  public onKeyDown(keyDown: KeyboardEvent): void {
    if (keyDown.code == 'Space') keyDown.stopPropagation();
  }

  onSubmitResendSpaceEmail(): void {}

  GetTextForLotRateUsageMetrics(rateNumber: number, rateType: string): string {
    let metricText: string = 'N/A';
    let ii: number = 0;
    let quantityUsed: number = 0;
    let limit: number = 0;

    try {
      //20240711 :: Ken Moss
      //Added to get the company Id from Form instead.
      const companyId: string = this.parkerEditForm.get('companyId')?.value ?? '';

      for (ii = 0; ii < this.lotRateUsageMetrics.length; ii++) {
        if (
          this.lotRateUsageMetrics[ii].rateNumber == rateNumber &&
          this.lotRateUsageMetrics[ii].companyId == companyId &&
          this.lotRateUsageMetrics[ii].rateType == rateType
        ) {
          quantityUsed = this.lotRateUsageMetrics[ii].used;
          limit = this.lotRateUsageMetrics[ii].limit;
        }
      }

      metricText = quantityUsed.toLocaleString() + '/' + limit.toLocaleString();
    } catch (err) {
      console.error('Error: ' + JSON.stringify(err));
      metricText = 'N/A';
    }

    return metricText;
  }

  parkerUpdateQueryResults(updatedParkerData: IParkerProfileDetail): void {
    let filteredParkers: IParkerProfileDetail[];

    filteredParkers = this.clonerService.deepClone(this.parkerStateService.filteredParkers);

    this.parkerStateService.updateFilteredParkers(updatedParkerData);
  }

  async onSubmitUpdateParkerProfile(): Promise<void> {
    const noDuplicateCardNumber: number = 0;
    const duplicateFoundAbort: number = 1;
    const duplicateFoundProceed: number = 2;
    let reassignCardNumber: boolean = false;
    let newAmanoOneMode: number = 0;

    if (this.isProcessingSave) {
      return;
    }

    // Checked before the banner goes up so a rejected date does not leave the screen spinning.
    if (!this.isEndDateWithinMaxValidated()) {
      return;
    }

    // 20260920 :: Ken Moss - Show progress before the Amano One pre-checks. They make HTTP calls, so the
    // banner previously did not appear until they had finished.
    this.isProcessingSave = true;
    this.processingMessage = 'Updating Parker Profile - Stand by...';

    if (this.hasParc) {
      // Check if the current lot's PARCS configuration is for Amano One before attempting to reassign the card number.
      if(this._appGeneralState.currentLotParcsConfiguration.parcsDisplayName.toUpperCase() == 'AMANO ONE'){
        const reassignResult: number = await this.reassignAmanoOneCardNumber();

        // The user declined the reassignment, so abandon the save entirely.
        if (reassignResult === duplicateFoundAbort) {
          this.isProcessingSave = false;
          return;
        }

        reassignCardNumber = reassignResult === duplicateFoundProceed;

        if (this.queueParkerReactivation === true) {
          if(await this.getAmanoOneAccessHolderData(this.parkerDetail.parkerId, this.parkerDetail.lotNumber)) {
            newAmanoOneMode = await this.displayReactivateDialog(this.amanoOneAccessHolder);
            if(!newAmanoOneMode) {
              this.isProcessingSave = false;
              return;
            }
          }
        }
      }
    }


    if (this.isValidParkerId(this.parkerDetail.parkerId)) {
      let parkerDataForUpdate: IParkerProfileUpdateRequest = this.setEditParkerModel(newAmanoOneMode);
      this.updateExistingParkerProfile(parkerDataForUpdate, reassignCardNumber);
    } else {
      this.isProcessingSave = false;
      this._dialogHelper.snackBarTopCenterTimed(
        'An error has occurred updaing this Parker. This Parker Profile HAS NOT BEEN updated. Please close your web browser and restart spACE AR',
        5000,
      );
    }
  }

  showSnackbarUpdatingMessage(): void {
    let action: any;
    const verticalPosition: MatSnackBarVerticalPosition = 'top';
    this.snackBar.open('Updating Parker Profile....', undefined, {
      duration: 0,
      verticalPosition: verticalPosition,
    });
  }

  closeSnackbarUpdatingMessage(): void {
    this.snackBar.dismiss();
  }

  setHeaderLine(): void {
    let lastFiveParkerId: string = '';
    console.log('Parker Id on Set Header Line: ', this.parkerDetail);

    lastFiveParkerId = this.parkerDetail.parkerId.substring(
      this.parkerDetail.parkerId.length - 5,
      this.parkerDetail.parkerId.length,
    );

    this.headerLine =
      this.parkerDetail.lotNumber +
      ' - ' +
      this.parkerDetail.lastName +
      ', ' +
      this.parkerDetail.firstName +
      ' - ' +
      lastFiveParkerId;
  }

  onCancelParkerProfileEditData(): void {
    // this.parkerStateService.isParkerEditMode = false;
    this._appGeneralState.canChangeLot = true;
    this.parkerStateService.isEditModeEnabled = false;
    this.editFinishedEvent.emit(this.parkerDetail);
  }

  checkKeyCardIsValid(): void {
    let cardNumber: string = this.parkerEditForm.get('cardNumber')!.value;

    if (this.keyCardValidationDebounceTimer !== null) {
      clearTimeout(this.keyCardValidationDebounceTimer);
    }

    if (cardNumber == '') {
      // don't need to validate a blank card number 20250821 :: Ken Moss
      this.parkerEditForm.controls['cardNumber'].setErrors(null);
      return;
    }

    // only validate if this lot isn't connect to a PARCS
    if (this.hasParc == false) {
      this.keyCardValidationDebounceTimer = setTimeout(() => {
        this.validateCardNumberAvailable(this.parkerDetail.lotNumber, cardNumber);
      }, 500);
    }
  }

  validateCardNumberAvailable(lotNumber: string, cardNumber: string): void {
    let keyCardExists: boolean = false;

    cardNumber = cardNumber.trim();

    if (cardNumber === '0' || cardNumber === '') return;

    this.dataService.getKeyCardExistsByLotNumberCardNumber(lotNumber, cardNumber, this.parkerDetail.parkerId).subscribe(
      (res) => {
        keyCardExists = res;
      },
      (err: any) => {},
      () => {
        if (keyCardExists) this.parkerEditForm.controls['cardNumber'].setErrors({ cardInvalid: true });
        this.parkerEditForm.updateValueAndValidity();
      },
    );
  }

  initBillingStateList(): void {
    let stateList = new AceStates();

    this.billingStates = stateList.AceUSStateList();
  }

  initVehicleStateList(): void {
    let stateList = new AceStates();

    this.vehicleStates = stateList.AceUSStateList();
  }

  initAdditionalVehicleStateList(): void {
    let stateList = new AceStates();

    this.additionalVehicleStates = stateList.AceUSStateList();
  }

  // 20260919 :: Ken Moss - Now returns the IParkerProfileUpdateRequest DTO instead of a full
  // IParkerProfileDetail. The server derives tenantId, isTermDateSet and active itself, and ignores
  // the display/aggregate fields, so they are no longer built or sent.
  // 20260920 :: amanoOneReactivationModeId is 0 unless the reactivation dialog supplied a mode.
  setEditParkerModel(amanoOneReactivationModeId: number = 0): IParkerProfileUpdateRequest {
    const endDateNotSetFlag: Date = new Date('0001-01-01T00:00:00');
    const companyId: string = this.parkerEditForm.get('companyId')!.value;
    const cardNumber: string = this.trimLeadingZerosFromCardNumber(this.parkerEditForm.get('cardNumber')!.value);
    const endDateValue = this.parkerEditForm.get('endDate')!.value;

    let endDate: Date = endDateValue;
    let taxExempt: boolean = false;

    // Carried over verbatim from the original builder: taxExempt is only evaluated when an end date is present.
    if (endDateValue === '' || endDateValue === null) {
      endDate = endDateNotSetFlag;
    } else if (this.parkerEditForm.get('isTaxExempt')!.value === '') {
      taxExempt = false;
    } else {
      taxExempt = true;
    }

    return {
      parkerId: this.sqlSafeWrap(this.parkerDetail.parkerId),
      lotNumber: this.sqlSafeWrap(this.parkerDetail.lotNumber),
      companyId: companyId,
      companyName: this.sqlSafeWrap(this.companyList.find((x) => x.companyId == companyId)!.companyName),
      lastName: this.sqlSafeWrap(this.parkerEditForm.get('lastName')!.value),
      firstName: this.sqlSafeWrap(this.parkerEditForm.get('firstName')!.value),
      address: this.sqlSafeWrap(this.parkerEditForm.get('billingAddress1')!.value),
      addressLine: this.sqlSafeWrap(this.parkerEditForm.get('billingAddressLine')!.value),
      city: this.sqlSafeWrap(this.parkerEditForm.get('billingCity')!.value),
      state: this.sqlSafeWrap(this.parkerEditForm.get('billingState')!.value),
      zipCode: this.sqlSafeWrap(this.parkerEditForm.get('billingZipCode')!.value),
      phone: this.sqlSafeWrap(this.parkerEditForm.get('phone')!.value),
      emailAddress: this.sqlSafeWrap(this.parkerEditForm.get('primaryEmailAddress')!.value),
      cardNumber: this.sqlSafeWrap(cardNumber),
      vehicleColor: this.sqlSafeWrap(this.parkerEditForm.get('vehicleColor')!.value),
      vehicleMake: this.sqlSafeWrap(this.parkerEditForm.get('vehicleMake')!.value),
      vehicleModel: this.sqlSafeWrap(this.parkerEditForm.get('vehicleModel')!.value),
      vehicleState: this.sqlSafeWrap(this.parkerEditForm.get('vehicleState')!.value),
      vehicleLicense: this.sqlSafeWrap(this.parkerEditForm.get('vehicleLicense')!.value),
      payType: 'todo - get pay type',
      rateNumber: this.parkerEditForm.get('rateNumber')!.value,
      taxExempt: taxExempt,
      deliveryType: this.sqlSafeWrap(this.parkerEditForm.get('invoiceDeliveryMethod')!.value),
      parkerPayType: this.sqlSafeWrap(this.parkerEditForm.get('parkerPayType')!.value),
      userName: this.sqlSafeWrap(this._appGeneralState.userName),
      stallNumber: this.sqlSafeWrap(this.parkerEditForm.get('stallNumber')!.value),
      comments: this.sqlSafeWrap(this.parkerEditForm.get('comments')!.value),
      startDate: this.parkerEditForm.get('startDate')!.value,
      endDate: endDate,
      parcsType: this.parcsTypeForCurrentLot(),
      amanoOneReactivationModeId: amanoOneReactivationModeId > 0 ? amanoOneReactivationModeId : null,
    };
  }

  // 20260919 :: Ken Moss - The grid and parker state still need the full detail shape, so overlay the
  // saved values on the pre-edit profile now that setEditParkerModel only builds the narrow DTO.
  buildUpdatedParkerDetail(updateRequest: IParkerProfileUpdateRequest): IParkerProfileDetail {
    return {
      ...this.parkerDetail,
      ...updateRequest,
      isTermDateSet: true,
      tenantId:
        updateRequest.parkerPayType.toUpperCase() === 'PARKER' ? updateRequest.parkerId : updateRequest.companyId,
    };
  }

  // 20260919 :: Ken Moss - Maps the lot's PARCS display name to the discriminator the API branches on.
  // Previously the update payload sent no ParcsType at all, which the API defaulted to the Amano path.
  parcsTypeForCurrentLot(): string {
    if (!this.hasParc) return '';

    const displayName: string =
      this._appGeneralState.currentLotParcsConfiguration.parcsDisplayName?.toUpperCase() ?? '';

    if (displayName === 'AMANO ONE') return 'AmanoOne';
    if (displayName === 'FLASH') return 'Flash';

    return '';
  }

  getMarketRateListByLotNumberOnRateChange(lotNumber: string) {
    this.dataService.getMarketRatesForLotNumber(lotNumber).subscribe(
      (res) => {
        this.marketRateList = res;
      },
      (err: any) => {},
      () => {
        this.parkerEditForm.patchValue({
          rateNumber: this.parkerDetail.rateNumber.toLocaleString(),
        });
      },
    );
  }

  getMarketRateListByLotNumber(lotNumber: string) {
    this.dataService.getMarketRatesForLotNumber(lotNumber).subscribe(
      (res) => {
        this.marketRateList = res;
      },
      (err: any) => {},
      () => {
        this.parkerEditForm.patchValue({
          rateNumber: this.parkerDetail.rateNumber.toLocaleString(),
        });
        this.setParkerFormData();
      },
    );
  }

  // 20260919 :: Ken Moss - Takes the narrow update DTO; the full detail object for the grid/state is
  // rebuilt in the completion handler.
  updateExistingParkerProfile(parkerUpdateRequest: IParkerProfileUpdateRequest, reassignCardNumber: boolean): void {
    let updateResponse: IParkerUpdateStatusResponse;
    this.isProcessingSave = true;
    this.processingMessage = 'Updating Parker Profile - Stand by...';
    this.dataService.updateExistingParkerProfile(parkerUpdateRequest, reassignCardNumber).subscribe(
      (res) => {
        updateResponse = res;
      },
      (err: any) => {
        let errorNumberMessage: string = '';
        const updateError: IParkerUpdateStatusResponse | null = err?.error ?? null;
        this.isProcessingSave = false;
        if (updateError?.nativeErrorMessage) errorNumberMessage = `Error Reference: ${updateError.nativeErrorMessage}`;
        else if (updateError?.errorMessage) errorNumberMessage = `Error Reference: ${updateError.errorMessage}`;
        else if (err.error != null || undefined) errorNumberMessage = `Error Reference: ${err.error}`;

        if (updateError?.isParcsError) {
          this._dialogHelper.displayGenericErrorDialog(
            'PARCS System Error',
            'An error occured updating the Parker Profile.  This parker was not saved.',
            errorNumberMessage,
          );
        } else {
          this._dialogHelper.displayGenericErrorDialog(
            'spACE-AR System Error',
            'An error occured updating the Parker Profile.  This parker was not saved.',
            errorNumberMessage,
          );
        }
      },
      () => {
        this.isProcessingSave = false;

        const parkerProfileData: IParkerProfileDetail = this.buildUpdatedParkerDetail(parkerUpdateRequest);

        this.updateExistingVehicles(parkerProfileData);
        this.setEditParkerData(parkerProfileData);
        this.parkerUpdateQueryResults(parkerProfileData);

        this.parkerStateService.setParkerDetail(parkerProfileData);
        this.parkerStateService.updateParkerQueryData(parkerProfileData);
        this.editFinishedEvent.emit(parkerProfileData);
        this.parkerStateService.isEditModeEnabled = false;
        this._appGeneralState.refreshParkerProfileGrid(); //This forces an update on the Parker-VS-Detail grid to display any recently changed data.
        // A 207 Multi-Status lands here (parker saved, vehicle LPR sync failed) and sets isVehicleError=true.
        if (updateResponse?.isVehicleError)
          this._dialogHelper.snackBarTopCenterTimed(
            'This Parker Profile has been successfully updated, but the vehicle data was not updated to the LPR.',
            7000,
          );
        else this._dialogHelper.snackBarTopCenterTimed('This Parker Profile has been successfully updated', 3000);
      },
    );
  }

  // If the start or end date has changed, then update all vehicles with the new dates
  // Helper function to compare only date portions (ignoring time)
  private isSameDate(date1: Date | string, date2: Date | string): boolean {
    const d1 = new Date(date1);
    const d2 = new Date(date2);

    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
  }

  // If the start or end date has changed, then update all vehicles with the new dates
  doesUpdateRequireVehicleUpdate(newStartDate: Date, newEndDate: Date): boolean {
    let status: boolean = false;

    const startDateChanged: boolean = !this.isSameDate(this.parkerDetail.startDate, newStartDate);
    const endDateChanged: boolean = !this.isSameDate(this.parkerDetail.endDate, newEndDate);

    console.log('Existing Start Date: ', this.parkerDetail.startDate);
    console.log('Existing End Date: ', this.parkerDetail.endDate);
    console.log('New Start Date: ', newStartDate);
    console.log('New End Date: ', newEndDate);
    console.log('Start Date Changed: ', startDateChanged);
    console.log('End Date Changed: ', endDateChanged);

    // Return true if either date has changed
    status = startDateChanged || endDateChanged;
    return status;
  }

  // Updates any vehicles with the new start and end date if either of those dates were changed on the parker profile
  updateExistingVehicles(parkerProfileData: IParkerProfileDetail): void {
    const status = this.doesUpdateRequireVehicleUpdate(parkerProfileData.startDate, parkerProfileData.endDate);

    if (status) {
      console.log('Updating Vehicles with new start and end date');
    } else {
      console.log('No need to update vehicles with new start and end date');
    }
  }

  setEditParkerData(updatedParkerData: IParkerProfileDetail): void {
    let parkerData: IParkerProfileDetail = {} as IParkerProfileDetail;

    let aStallType: IStallTypes;

    aStallType = this.stallDescriptions.find((x) => x.rateNumber == +updatedParkerData.rateNumber)!;

    parkerData.parkerId = updatedParkerData.parkerId;
    parkerData.lotNumber = this.parkerDetail.lotNumber;
    parkerData.companyId = updatedParkerData.companyId;
    parkerData.lastName = updatedParkerData.lastName;
    parkerData.firstName = updatedParkerData.firstName;
    parkerData.address = updatedParkerData.address;
    parkerData.addressLine = updatedParkerData.addressLine;
    parkerData.city = updatedParkerData.city;
    parkerData.state = updatedParkerData.state;
    parkerData.zipCode = updatedParkerData.zipCode;
    parkerData.phone = updatedParkerData.phone;
    parkerData.emailAddress = updatedParkerData.emailAddress;
    parkerData.active = updatedParkerData.active;
    parkerData.cardNumber = this.trimLeadingZerosFromCardNumber(updatedParkerData.cardNumber);
    parkerData.vehicleColor = updatedParkerData.vehicleColor;
    parkerData.vehicleMake = updatedParkerData.vehicleMake;
    parkerData.vehicleModel = updatedParkerData.vehicleColor;
    parkerData.vehicleLicense = updatedParkerData.vehicleLicense;
    parkerData.vehicleState = updatedParkerData.vehicleState;
    parkerData.payType = updatedParkerData.payType;
    parkerData.rateNumber = +updatedParkerData.rateNumber;
    parkerData.deliveryType = updatedParkerData.deliveryType;
    parkerData.parkerPayType = updatedParkerData.parkerPayType;
    parkerData.startDate = updatedParkerData.startDate;
    parkerData.endDate = updatedParkerData.endDate;
    parkerData.userName = updatedParkerData.userName;
    parkerData.stallNumber = updatedParkerData.stallNumber;
    parkerData.isTermDateSet = updatedParkerData.isTermDateSet;
    parkerData.comments = updatedParkerData.comments;
    parkerData.tenantId = updatedParkerData.tenantId;
    parkerData.companyName = this.companyList.find((x) => x.companyId == parkerData.companyId)!.companyName;
    parkerData.rateDescription = aStallType.description;
    parkerData.stallType = aStallType.parkingStallType;
    parkerData.rateDescriptionSummary = this.rateDescriptionSummary;

    this.addedParkerData = parkerData;
  }

  trimLeadingZerosFromCardNumber(cardNumber: string): string {
    return cardNumber.replace(/^0+/, '');
  }

  setRateDescriptionData(): string {
    let rateNumber: number = 0;
    let rateDescriptionSummary: string = '';

    rateNumber = this.parkerDetail.rateNumber;

    if (rateNumber > 0) {
      let aStallType: IStallTypes;
      let rateData!: ILotRateList;

      aStallType = this.stallDescriptions.find((x) => x.rateNumber == rateNumber)!;

      let ii: number = 0;
      for (ii = 0; ii < this.marketRateList.length; ii++) {
        if (this.marketRateList[ii].rateNumber == rateNumber) {
          rateData = this.marketRateList[ii];
        }
      }

      this.description = rateData.description;
      this.stallType = aStallType.parkingStallType;

      this.parkerEditForm.patchValue({
        rateNumber: rateNumber,
        stallType: this.stallType,
      });

      if (aStallType.taxExempt === 'Y') {
        rateDescriptionSummary = `${aStallType.rateType}  ${rateData.displayRate} - ${rateData.description}`;
      } else {
        rateDescriptionSummary = `${aStallType.rateType}  ${rateData.displayRate} - ${rateData.description}`;
      }
    }

    return rateDescriptionSummary;
  }

  evalDateChange() {
    console.log('evalDateChange called');
  }

  /// Last line of defence for the End Date ceiling - the datepicker can be bypassed by typing.
  isEndDateWithinMaxValidated(): boolean {
    if (!isParkerEndDateBeyondMax(this.parkerEditForm.get('endDate')!.value)) {
      return true;
    }

    this.parkerEditForm.controls['endDate'].setErrors({
      ...(this.parkerEditForm.controls['endDate'].errors ?? {}),
      maxEndDate: true,
    });

    this._dialogHelper.displayGenericErrorDialog(
      'Invalid End Date',
      `The end date cannot be later than ${this.maxEndDateDisplay}.`,
      'Correct the end date and submit again.',
    );

    return false;
  }

  onEvaluateStartEndDate(): void {
    console.log('start onEvaluateStartEndDate');

    try {
      // Any initialization or pre-checks can go here
    
      let startDateEval: Date;
      let endDateEval: Date;

      let newStartDate: Date;
      let newEndDate: Date;

      startDateEval = this.parkerEditForm.get('startDate')!.value;
      endDateEval = this.parkerEditForm.get('endDate')!.value;

      newStartDate = new Date(startDateEval);
      newEndDate = new Date(endDateEval);

      if (newEndDate.toLocaleString() === '12/31/1969, 4:00:00 PM') {
        return;
      }

      if (newEndDate.toLocaleString() !== '') {
        if (newStartDate.toLocaleString() === '') {
          this.parkerEditForm.controls['endDate'].setErrors({ endDate: true });
        } else {
          if (newEndDate < newStartDate) {
            this.parkerEditForm.controls['endDate'].setErrors({ endDate: true });
          }
        }

        if (isParkerEndDateBeyondMax(endDateEval)) {
          // Spread the existing errors so a datepicker max error set by [max] is not wiped out here.
          this.parkerEditForm.controls['endDate'].setErrors({
            ...(this.parkerEditForm.controls['endDate'].errors ?? {}),
            maxEndDate: true,
          });
        }
      }

      console.log('New Date', new Date());
      console.log('currentEndDate', this.currentEndDate);
      console.log('newEndDate', newEndDate);
      console.log('currentEndDateDeactivated',(new Date(this.currentEndDate) < new Date()));
      console.log('newEndDateIsAfterCurrentDate', newEndDate > new Date());

      /// Evaluate whether the Parker is being reactivated after being termed.
      if((new Date(this.currentEndDate) < new Date()) && (newEndDate > new Date())){
        this.queueParkerReactivation = true;
      }
      else
        this.queueParkerReactivation = false;

    console.log('Queue Parker Reactivation:', this.queueParkerReactivation);
    
     } catch (err) {
      console.error('Error in onEvaluateStartEndDate: ' + err);
    }
  }

  setParkerFormData(): void {
    console.log('setParkerFormData');
    let isDateValid: boolean;
    var checkInvalidDateString = this.parkerDetail.endDate.toLocaleString().substring(0, 10);

    isDateValid = !(checkInvalidDateString === '0001-01-01' || checkInvalidDateString === '1/1/1, 12:');

    let rateDescriptionSummary: string = '';
    try {
      rateDescriptionSummary = this.setRateDescriptionData();
    } catch (err) {
      console.error('Error getting rate Description on setParkerFormData: ' + err);
    }

    let taxExempt: string = '';
    let activeStatus: string = 'A';

    if (this.parkerDetail.taxExempt) taxExempt = 'Yes';
    else taxExempt = 'No';
    try {
      this.parkerEditForm.patchValue({
        isActive: this.parkerDetail.active,
        companyId: this.parkerDetail.companyId,
        lastName: this.parkerDetail.lastName,
        firstName: this.parkerDetail.firstName,
        primaryEmailAddress: this.parkerDetail.emailAddress,
        startDate: this.parkerDetail.startDate,
        cardNumber: this.trimLeadingZerosFromCardNumber(this.parkerDetail.cardNumber),
        stallNumber: this.parkerDetail.stallNumber,
        rateNumber: this.parkerDetail.rateNumber.toLocaleString(),
        isTaxExempt: taxExempt,
        parkerPayType: this.parkerDetail.parkerPayType,
        invoiceDeliveryMethod: this.parkerDetail.deliveryType,
        billingAddress1: this.parkerDetail.address,
        billingAddressLine: this.parkerDetail.addressLine,
        billingCity: this.parkerDetail.city,
        billingState: this.parkerDetail.state,
        billingZipCode: this.parkerDetail.zipCode,
        vehicleModel: this.parkerDetail.vehicleModel,
        vehicleMake: this.parkerDetail.vehicleMake,
        vehicleColor: this.parkerDetail.vehicleColor,
        vehicleState: this.parkerDetail.vehicleState,
        vehicleLicense: this.parkerDetail.vehicleLicense,
        comments: this.parkerDetail.comments,
        phone: this.parkerDetail.phone,
        description: this.description,
        stallType: this.stallType,
        rateDescription: rateDescriptionSummary,
        startDateDisplay: this.parkerDetail.startDateDisplay,
        endDateDisplay: this.patchedUpEndDate,
        endDate: '',
      });

      if (isDateValid) {
        this.parkerEditForm.patchValue({
          endDate: this.parkerDetail.endDate,
        });
      }
      this.loading = false;
    } catch (err) {
      console.error('error setting form on parker-detail.ts');
    }
  }

  getQSRRowBackgroundColor(rowNumber: number): string {
    if (this.currentlyActiveRow === rowNumber) {
      return 'yellow';
    } else {
      if (rowNumber % 2 === 1) return '#e1e1e4';
      else return '#fff';
    }
  }

  initVehicleForm(): void {
    try {
      this.vehicleForm = new FormGroup({
        additionalVehicleLicensePlate: new FormControl('', [Validators.required]),
        additionalVehicleMake: new FormControl('', []),
        additionalVehicleModel: new FormControl('', []),
        additionalVehicleColor: new FormControl('', []),
        additionalVehicleState: new FormControl('', []),
      });
    } catch (err) {
      console.error('Error on init vehicle edit form ' + JSON.stringify(err));
    }
  }

  onResetAdditionalVehicleData(): void {
    this.vehicleForm.patchValue({
      additionalVehicleLicensePlate: '',
      additionalVehicleMake: '',
      additionalVehicleModel: '',
      additionalVehicleColor: '',
      additionalVehicleState: '',
    });
  }
  onResetAdditionalEmailData(): void {
    this.emailForm.patchValue({
      additionalEmailAddress: '',
      additionalEmailName: '',
      additionalEmailTitle: '',
      additionalEmailcomments: '',
    });
  }

  initEmailForm(): void {
    try {
      this.emailForm = new FormGroup({
        additionalEmailAddress: new FormControl('', [Validators.required]),
        additionalEmailName: new FormControl('', []),
        additionalEmailTitle: new FormControl('', []),
        additionalEmailcomments: new FormControl('', []),
      });
    } catch (err) {
      console.error('Error on init vehicle edit form ' + JSON.stringify(err));
    }
  }

  initParkerForm(): void {
    try {
      this.parkerEditForm = new FormGroup({
        isActive: new FormControl('', []),
        companyId: new FormControl('', [Validators.required]),
        lastName: new FormControl('', [Validators.required]),
        firstName: new FormControl('', [Validators.required]),
        primaryEmailAddress: new FormControl('', []),
        startDate: new FormControl('', [Validators.required]),
        endDate: new FormControl('', []),
        cardNumber: new FormControl('', []),
        stallNumber: new FormControl('', []),
        rateNumber: new FormControl('', [Validators.required]),
        isTaxExempt: new FormControl('', []),
        parkerPayType: new FormControl('', [Validators.required]),
        invoiceDeliveryMethod: new FormControl('', [Validators.required]),
        billingAddress1: new FormControl('', []),
        billingAddressLine: new FormControl('', []),
        billingCity: new FormControl('', []),
        billingState: new FormControl('', []),
        billingZipCode: new FormControl('', []),
        vehicleModel: new FormControl('', []),
        vehicleMake: new FormControl('', []),
        vehicleColor: new FormControl('', []),
        vehicleState: new FormControl('', []),
        vehicleLicense: new FormControl('', []),
        comments: new FormControl('', []),
        phone: new FormControl('', []),
        description: new FormControl('', []),
        stallType: new FormControl('', []),
        rateDescription: new FormControl('', []),
        startDateDisplay: new FormControl('', []),
        endDateDisplay: new FormControl('', []),
      });

      this.watchStartEndDateChanges();
    } catch (err) {
      console.error('Error on init parker edit form ' + JSON.stringify(err));
    }
  }

  // 20260919 :: Ken Moss - Replaces the (blur)/(onChange) bindings on the date inputs. valueChanges fires
  // for typing, paste, and datepicker selection; the DOM events missed some of those paths.
  watchStartEndDateChanges(): void {
    this.parkerEditForm
      .get('startDate')!
      .valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.onEvaluateStartEndDate());

    this.parkerEditForm
      .get('endDate')!
      .valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.onEvaluateStartEndDate());
  }

  onNavtoEditParker(parkerId: string): void {
    this.router.navigate([`/parker/${parkerId}/edit`]);
  }

  getCompanyAndMarketRateListByLotNumber(lotNumber: string, companyId: string) {
    this.dataService.getMarketCompanyRatesForLotAndCompany(lotNumber, companyId).subscribe(
      (res) => {
        this.marketRateList = res;
      },
      (err: any) => {},
      () => {
        this.parkerEditForm.patchValue({
          rateNumber: this.parkerDetail.rateNumber.toLocaleString(),
        });
        this.setParkerFormData();
        //this.setRateDescriptionData();
      },
    );
  }

  getCompanyAndMarketRateListByLotNumberOnRateChange(lotNumber: string, companyId: string) {
    this.dataService.getMarketCompanyRatesForLotAndCompany(lotNumber, companyId).subscribe(
      (res) => {
        this.marketRateList = res;
      },
      (err: any) => {},
      () => {
        this.parkerEditForm.patchValue({
          rateNumber: this.parkerDetail.rateNumber.toLocaleString(),
        });
      },
    );
  }

  checkIfFormIsDirty(): any {
    this.parkerEditForm.valueChanges.subscribe((res) => {
      this.isFormDirty = this.parkerEditForm.dirty;
    });
  }

  onRouteToParkersHome(): void {
    if (this.isFormDirty) {
      let responseFromConfirm: any;

      const dialogRef = this.dialog.open(DialogConfirmNavigateAwayParkerComponent);
      dialogRef.afterClosed().subscribe((result) => {
        responseFromConfirm = result;
        if (responseFromConfirm) this.router.navigate([`/parkerlist`]);
      });
    } else this.router.navigate([`/parkerlist`]);
  }

  keyPressAlphanumeric(event: any): boolean {
    var inp = String.fromCharCode(event.keyCode);

    // if (keyDown.code == 'Space') keyDown.stopPropagation();

    // if (/[a-zA-Z0-9]/.test(inp)) {
    if (/^[a-zA-Z0-9\_\- ]*$/.test(inp)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  keyPressCardNumber(event: any): boolean {
    var inp = String.fromCharCode(event.keyCode);

    if (/[a-zA-Z0-9\-\._:]/.test(inp)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  keyPressNumeric(event: any): boolean {
    var inp = String.fromCharCode(event.keyCode);

    if (/[0-9]/.test(inp)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  validatePaste(e: any): boolean {
    const copiedText = e.clipboardData.getData('text');
    if (/[a-zA-Z0-9]/.test(copiedText) == false) {
      e.preventDefault(); //this line executes if copiedText has any invalid characters
      return false;
    } else return true;
  }

  onRateTypeChange(): void {
    let rateNumber: number = 0;
    let rateData: ILotRateList;
    let taxExemptFlag: string = '';

    rateNumber = this.parkerEditForm.get('rateNumber')!.value;

    if (rateNumber <= 0) {
      this.parkerEditForm.patchValue({
        description: 'Select rate to view stall type',
        stallType: 'Select rate to view description',
        isTaxExempt: 'Select rate',
      });
    } else {
      let aStallType: IStallTypes;
      let rateData!: ILotRateList;

      aStallType = this.stallDescriptions.find((x) => x.rateNumber == rateNumber)!;

      let ii: number = 0;
      for (ii = 0; ii < this.marketRateList.length; ii++) {
        if (this.marketRateList[ii].rateNumber == rateNumber) {
          rateData = this.marketRateList[ii];
        }
      }

      if (rateData.rateType == 'M') taxExemptFlag = 'N';
      else {
        if (rateData.taxExempt == true) taxExemptFlag = 'Y';
        else taxExemptFlag = 'N';
      }

      this.setRateDescriptionStallTypeValues(aStallType.parkingStallType, aStallType.description, taxExemptFlag);

      if (aStallType.taxExempt === 'Y') {
        this.rateDescriptionSummary = `${aStallType.rateType}  ${rateData.displayRate} - ${rateData.description}`;
      } else {
        this.rateDescriptionSummary = `${aStallType.rateType}  ${rateData.displayRate} - ${rateData.description}`;
      }
    }
  }

  onCompanyNumberChange(): void {
    let companyId: string = '';
    let currentRateNumber: number;

    currentRateNumber = this.parkerEditForm.get('rateNumber')!.value;
    companyId = this.parkerEditForm.get('companyId')!.value;

    if (companyId === '') {
      this.marketRateList = [];
      this.parkerEditForm.patchValue({
        rateNumber: '',
        isTaxExempt: 'Select Rate',
      });
      this.getMarketRateListByLotNumberOnRateChange(this.parkerDetail.lotNumber);
    } else {
      this.getCompanyAndMarketRateListByLotNumberOnRateChange(this.parkerDetail.lotNumber, companyId);
    }

    if (this.isCompanyRate(currentRateNumber)) {
      this.parkerEditForm.patchValue({
        rateNumber: '',
        isTaxExempt: 'Select Rate',
        description: 'Select rate to view stall type',
        stallType: 'Select rate to view description',
      });
    }
  }

  isCompanyRate(rateNumber: number): boolean {
    let aCompanyRate: boolean = false;
    let ii: number;

    for (ii = 0; ii < this.marketRateList.length; ii++) {
      if (rateNumber === this.marketRateList[ii].rateNumber) {
        if (this.marketRateList[ii].rateType === 'C') aCompanyRate = true;
      }
    }

    return aCompanyRate;
  }

  setRateDescriptionStallTypeValues(stallType: string, rateDescription: string, taxExempt: string): void {
    let taxExemptDisplayValue: string = 'Yes';

    if (taxExempt === 'N') taxExemptDisplayValue = 'No"';

    this.parkerEditForm.patchValue({
      description: rateDescription,
      stallType: stallType,
      isTaxExempt: taxExemptDisplayValue,
    });
  }

  sqlSafeWrap(columnValue: string): string {
    let safeString: string = '';

    if (columnValue === null) {
      safeString = '';
    } else safeString = columnValue;

    return safeString;
  }

  /* =================================
     DATA INIT SEQUENCE
  */

  onInitDataFetch(): void {
    this.getCompanyListForLotNumber(this.parkerDetail.lotNumber);
  }

  getCompanyListForLotNumber(lotNumber: string) {
    this.dataService.getCompanyListForLotNumber(lotNumber).subscribe(
      (companyList) => {
        this.companyList = companyList;
      },
      (err: any) => {},
      () => {
        this.parkerStateService.companyList = this.companyList;
        this.getStallDescriptionListForLotNumber(this.parkerDetail.lotNumber);
      },
    );
  }

  getStallDescriptionListForLotNumber(lotNumber: string) {
    this.dataService.getLotStallDescriptionList(lotNumber).subscribe(
      (res) => {
        this.stallDescriptions = res;
      },
      (err: any) => {},
      () => {
        this.getAllAdditionalVehiclesForParker(this.parkerDetail.parkerId);
      },
    );
  }

  getAllAdditionalVehiclesForParker(parkerId: string): void {
    this.dataService.getAdditionalVehiclesForParker(parkerId).subscribe(
      (res) => {
        this.additionalVehicles = res;
      },
      (err: any) => {},
      () => {
        this.getAllAdditionalEmailsForParker(this.parkerDetail.parkerId);
      },
    );
  }

  getAllAdditionalEmailsForParker(parkerId: string): void {
    this.dataService.getAdditionalEmailsForParker(parkerId).subscribe(
      (res) => {
        this.additionalEmails = res;
      },
      (err: any) => {},
      () => {
        this.getSpaceEmailsForParkerId(this.parkerDetail.parkerId);
      },
    );
  }

  getSpaceEmailsForParkerId(entityId: string): void {
    this.dataService.getSpaceEmailUsersByEntityId(entityId).subscribe({
      next: (results) => (this.spaceEmails = results),
      complete: () => {
        this.onInitSetStartupParkerFormData();
      },
    });
  }

  // Returns true if the user confirmed the card number reassignment, false otherwise.
  async displayDialogCardNumberReassign(
    amanoParkerData: IAmanoParkerDataForCardReassignmentResponse,
  ): Promise<boolean> {
    let parkerName: string = amanoParkerData.lastName + ', ' + amanoParkerData.firstName;
    // Use the card number entered on the form (the value passed to the lookup sprocs); the API response
    // omits cardNumber when the Amano access holder has no associated spACE-AR parker.
    let cardNumber: string = this.parkerEditForm.get('cardNumber')!.value;
    let parkerId: string = amanoParkerData.parkerId;
    let isActive: boolean = amanoParkerData.active?.toUpperCase() == 'ACTIVE' ? true : false;

    const dialogRef = this._dialog.open(DialogCardNumberReassignComponent, {
      width: '650px',
      data: {
        parkerName,
        cardNumber,
        parkerId,
        isActive,
      },
    });

    const result: boolean = (await firstValueFrom(dialogRef.afterClosed())) === true;
    console.log(result ? 'User confirmed card reassignment.' : 'User canceled card reassignment.');

    return result;
  }

  async reassignAmanoOneCardNumber(): Promise<number> {
    let returnValue: number = 0;
    const noDuplicateCardNumber: number = 0;
    const duplicateFoundAbort: number = 1;
    const duplicateFoundProceed: number = 2;
    let reassignCardNumber: boolean = false;
    let cardNumber: string = this.parkerEditForm.get('cardNumber')!.value;
    const accessHolderId: string = await this.getAmanoOneAccessHolderForCardNumberLotNumber(
      this._appGeneralState.currentLotNumber,
      cardNumber,
    );
    if (accessHolderId != '') {
      const amanoParkerData: IAmanoParkerDataForCardReassignmentResponse | null =
        await this.getAmanoOneParkerDataForCardReassignment(accessHolderId);
      if (amanoParkerData) {
        // Hide the progress banner while the user answers, then restore it only if the save continues.
        this.isProcessingSave = false;
        const proceedWithReassignment: boolean = await this.displayDialogCardNumberReassign(amanoParkerData);

        if (proceedWithReassignment) {
          this.isProcessingSave = true;
          this.isCardReassignmentPending = true;
          returnValue = duplicateFoundProceed;
        } else {
          this.isCardReassignmentPending = false;
          returnValue = duplicateFoundAbort;
        }
      }
    }
    return returnValue;
  }

  /// Gets the accessHolderId for a given lot number and card number.
  // This is used to check if an AccessHolder profile already exists for the card number being added to the Parker profile.
  async getAmanoOneAccessHolderForCardNumberLotNumber(lotNumber: string, cardNumber: string): Promise<string> {
    try {
      return await firstValueFrom(
        this.dataService.getAmanoOneAccessHolderforCardNumberLotNumberForUpdate(
          this.parkerDetail.parkerId,
          lotNumber,
          cardNumber,
        ),
      );
    } catch (err) {
      return '';
    }
  }

  async getAmanoOneParkerDataForCardReassignment(
    accessHolderId: string,
  ): Promise<IAmanoParkerDataForCardReassignmentResponse | null> {
    try {
      return await firstValueFrom(this.dataService.getAmanoOneParkerDataForCardReassignment(accessHolderId));
    } catch (err) {
      return null;
    }
  }

  setParcsSetting(): void {
    this.hasParc = Object.keys(this._appGeneralState.currentLotParcsConfiguration).length !== 0;
    // if (this.hasParc) {
    //   this.parcsId = this._appGeneralState.currentLotParcsConfiguration.parcsId.toUpperCase();
    //   this.amanoOneConfigSettings = JSON.parse(this._appGeneralState.currentLotParcsConfiguration.settings);
    // } else {
    //   this.parcsSaveButtonDisabled = true;
    // }
  }

  // MatDialog only reports its result asynchronously, so callers must await this.
  // Returns the chosen Amano One mode id, or 0 when the user cancels.
  async displayReactivateDialog(amanoOneAccessHolder: IAmanoOneAccessHolderUpdateResponse): Promise<number> {
    // Hide the progress banner while the user answers, then restore it only if the save continues.
    this.isProcessingSave = false;

    const dialogRef = this._dialog.open(DialogAmanoOneReactivateComponent, {
      width: '680px',
      height:  '420px',
      data: {
        amanoOneAccessHolder: amanoOneAccessHolder,
      },
    });

    // Both dialog buttons close with a mode id; Esc and backdrop clicks close with undefined.
    const dialogResult: number | undefined = await firstValueFrom(dialogRef.afterClosed());
    const selectedModeId: number = dialogResult ?? 0;

    if (selectedModeId > 0) {
      this.isProcessingSave = true;
    }

    return selectedModeId;
  }

    async getAmanoOneAccessHolderData(parkerId: string, lotNumber: string): Promise<boolean> {
      let accessHolder: IAmanoOneAccessHolderUpdateResponse;
      let status: boolean = true;
  
      try {
        accessHolder = await firstValueFrom(
          this._parcsDataService.getAmanoOneParcsAccessHolderByParkerId(parkerId, lotNumber),
        );
        this.amanoOneAccessHolder = accessHolder;
      } catch (err) {
        status = false;
      }
      return status;
    }
  
}
